import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import "./map-container.css";

import { Map } from "@vis.gl/react-maplibre";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

const TILESERVER = import.meta.env.VITE_TILE_SERVER;
const MAP_TOKEN = import.meta.env.VITE_MAP_TOKEN;

const MAP_STYLES = {
  light: `${TILESERVER}/styles/map_light/style.json`,
  dark: `${TILESERVER}/styles/map_dark/style.json`,
};

const tileTransformRequest = (url) => {
  if (url.startsWith(TILESERVER)) {
    // Add token as query param — works in both main thread AND worker thread
    const separator = url.includes("?") ? "&" : "?";
    return {
      url: `${url}${separator}token=${MAP_TOKEN}`,
      headers: { "X-FleetMaster-Token": MAP_TOKEN }, // keep header too for main thread requests
    };
  }
  return { url };
};

// Fetch style, inject token into tile URLs, return modified style
const fetchStyle = async (url) => {
  const res = await fetch(`${url}?token=${MAP_TOKEN}`, {
    headers: { "X-FleetMaster-Token": MAP_TOKEN },
  });
  const style = await res.json();

  // Inject token into each source's tile URLs
  for (const source of Object.values(style.sources)) {
    if (source.url) {
      source.url = `${source.url}${source.url.includes("?") ? "&" : "?"}token=${MAP_TOKEN}`;
    }
    if (source.tiles) {
      source.tiles = source.tiles.map(
        (t) => `${t}${t.includes("?") ? "&" : "?"}token=${MAP_TOKEN}`,
      );
    }
  }
  return style;
};

const useTheme = () => {
  const [theme, setTheme] = useState(
    () => document.body.getAttribute("data-theme") || "light",
  );
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setTheme(document.body.getAttribute("data-theme") || "light"),
    );
    observer.observe(document.body, { attributes: true });
    return () => observer.disconnect();
  }, []);
  return theme;
};

// ── Tracking — purely imperative, no useMap() ─────────────────────────────────
// const MapTracker = ({
//   map,
//   posLat,
//   posLng,
//   posBearing,
//   tracking,
//   onStopTracking,
//   onMapMoved,
// }) => {
//   // When tracking turns ON — snap to tilt + bearing immediately
//   useEffect(() => {
//     if (!map) return;

//     if (tracking && posLat != null && posLng != null) {
//       map.easeTo({
//         center: [posLng, posLat],
//         bearing: posBearing ?? 0,
//         pitch: 30,
//         zoom: 14,
//         duration: 600,
//       });
//     } else if (!tracking) {
//       // Reset pitch and bearing when tracking turns off
//       map.easeTo({ bearing: 0, pitch: 0, duration: 600 });
//     }
//   }, [tracking]); // only fires on tracking toggle, not every position update

//   // While tracking — follow position + keep bearing updated
//   useEffect(() => {
//     if (!map || !tracking || posLat == null || posLng == null) return;
//     map.easeTo({
//       center: [posLng, posLat],
//       bearing: posBearing ?? 0,
//       pitch: 30,
//       zoom: 14,
//       duration: 500,
//     });
//   }, [map, posLat, posLng, posBearing, tracking]);

//   // Stop tracking on drag
//   useEffect(() => {
//     if (!map) return;
//     const onDrag = () => {
//       onStopTracking?.();
//       onMapMoved?.();
//     };
//     map.on("dragstart", onDrag);
//     return () => map.off("dragstart", onDrag);
//   }, [map, onStopTracking, onMapMoved]);

//   return null;
// };
const MapTracker = ({
  map,
  posLat,
  posLng,
  posBearing,
  tracking,
  onStopTracking,
  onMapMoved,
}) => {
  useEffect(() => {
    if (!map) return;

    if (!tracking) {
      map.stop();

      map.easeTo({
        bearing: 0,
        pitch: 0,
        duration: 600,
      });

      return;
    }

    if (posLat == null || posLng == null) return;

    const bearing = Number(posBearing);

    map.stop();

    map.easeTo({
      center: [posLng, posLat],

      // Keep the user's current zoom while tracking.
      zoom: map.getZoom(),

      // Rotate only when valid bearing data exists.
      bearing: Number.isFinite(bearing) ? bearing : map.getBearing(),

      pitch: 30,
      duration: 500,
      essential: true,
    });
  }, [map, posLat, posLng, posBearing, tracking]);

  useEffect(() => {
    if (!map) return;

    const onDrag = () => {
      onStopTracking?.();
      onMapMoved?.();
    };

    map.on("dragstart", onDrag);

    return () => map.off("dragstart", onDrag);
  }, [map, onStopTracking, onMapMoved]);

  return null;
};

// ── Fit bounds ────────────────────────────────────────────────────────────────
const MapBoundsController = ({ map, markers }) => {
  const fittedRef = useRef(false);

  useEffect(() => {
    if (!map || fittedRef.current) return;
    const valid = (markers || []).filter(
      (m) =>
        m.position &&
        typeof m.position.lat === "number" &&
        typeof m.position.lng === "number",
    );
    if (!valid.length) return;

    const fit = () => {
      if (fittedRef.current) return;
      // Guard: ensure style is loaded before accessing sources
      if (!map.isStyleLoaded()) return;

      const bounds = new maplibregl.LngLatBounds();
      valid.forEach((m) => bounds.extend([m.position.lng, m.position.lat]));
      map.fitBounds(bounds, { padding: 80, animate: false });
      fittedRef.current = true;
    };

    if (map.isStyleLoaded()) fit();
    else map.once("styledata", fit);
  }, [map, markers]);

  return null;
};

// ── Zoom ──────────────────────────────────────────────────────────────────────
const CustomZoomControl = ({ map }) => (
  <div className="custom-zoom-control">
    <button className="zoom-btn" onClick={() => map?.zoomIn()} title="Zoom In">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>
    <div className="zoom-divider" />
    <button
      className="zoom-btn"
      onClick={() => map?.zoomOut()}
      title="Zoom Out"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
      >
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
    </button>
  </div>
);

const MapLoadingOverlay = ({ isLoading }) => {
  if (!isLoading) return null;
  return (
    <div className="map-loading-overlay">
      <div className="loading-content">
        <div className="loading-spinner-ring" />
        <p>Loading Fleet Data...</p>
      </div>
    </div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const MapContainer = forwardRef(
  (
    {
      children,
      center = [-33.8688, 151.2093],
      zoom = 12,
      markers = [],
      onMapReady,
      isLoading = false,
      isTracking,
      trackingPosition,
      onStopTracking,
      selectedFleet,
      className = "",
      onMapClick,
    },
    ref,
  ) => {
    // mapInstance in STATE so child components re-render when it becomes available
    const [mapInstance, setMapInstance] = useState(null);
    const [showRecenter, setShowRecenter] = useState(false);
    const theme = useTheme();
    const [mapStyle, setMapStyle] = useState(null);

    useEffect(() => {
      const styleUrl = MAP_STYLES[theme] ?? MAP_STYLES.light;
      fetchStyle(styleUrl).then(setMapStyle);
    }, [theme]);

    const handleMapRef = useCallback(
      (ref) => {
        const m = ref?.getMap?.();
        if (!m || m === mapInstance) return;

        // Wait for style to fully load before exposing the map instance
        if (m.isStyleLoaded()) {
          setMapInstance(m);
          onMapReady?.(m);
        } else {
          m.once("load", () => {
            setMapInstance(m);
            onMapReady?.(m);
          });
        }
      },
      [mapInstance, onMapReady],
    );

    const hasValidFleetLocation =
      selectedFleet?.lat != null && selectedFleet?.lng != null;

    useEffect(() => {
      setShowRecenter(hasValidFleetLocation);
    }, [selectedFleet, hasValidFleetLocation]);

    const handleMapReady = useCallback(
      (map) => {
        if (!map || map === mapInstance) return;
        setMapInstance(map);
        onMapReady?.(map);
      },
      [mapInstance, onMapReady],
    );

    const handleFitBounds = () => {
      if (!mapInstance || !markers.length) return;
      const valid = markers.filter(
        (m) =>
          m.position &&
          typeof m.position.lat === "number" &&
          typeof m.position.lng === "number",
      );
      if (!valid.length) return;
      const bounds = new maplibregl.LngLatBounds();
      valid.forEach((m) => bounds.extend([m.position.lng, m.position.lat]));
      mapInstance.fitBounds(bounds, { padding: 80, animate: true });
    };

    // use Forward ref to allow parent components to call exposed child functions
    useImperativeHandle(ref, () => ({
      fitBounds: handleFitBounds,
      zoomIn: () => mapInstance?.zoomIn(),
      zoomOut: () => mapInstance?.zoomOut(),
      flyTo: (lng, lat) => {
        mapInstance?.flyTo({
          center: [lng, lat],
          zoom: 15,
        });
      },
    }));

    const handleRecenterVehicle = useCallback(() => {
      if (!mapInstance || !hasValidFleetLocation) return;
      const { lat, lng } = selectedFleet;
      const point = mapInstance.project([lng, lat]);
      const offsetY = window.innerHeight * 0.25;
      const newCenter = mapInstance.unproject([point.x, point.y - offsetY]);
      mapInstance.easeTo({
        center: [newCenter.lng, newCenter.lat],
        zoom: mapInstance.getZoom(),
        duration: 800,
      });
      setShowRecenter(false);
    }, [mapInstance, selectedFleet, hasValidFleetLocation]);

    const handleMapMoved = useCallback(() => {
      if (hasValidFleetLocation) setShowRecenter(true);
    }, [hasValidFleetLocation]);

    return (
      <div className={`map-container-wrapper ${className}`}>
        {mapStyle && (
          <Map
            ref={handleMapRef}
            initialViewState={{
              longitude: center[1],
              latitude: center[0],
              zoom,
            }}
            style={{ width: "100%", height: "100%" }}
            //mapStyle={MAP_STYLES[theme] ?? MAP_STYLES.light}
            mapStyle={mapStyle}
            mapLib={maplibregl}
            attributionControl={false}
            transformRequest={tileTransformRequest}
            onClick={onMapClick}
          >
            {children}
          </Map>
        )}

        {/* Everything below receives mapInstance directly — no useMap() needed */}
        <MapBoundsController map={mapInstance} markers={markers} />
        <MapTracker
          map={mapInstance}
          posLat={trackingPosition?.lat}
          posLng={trackingPosition?.lng}
          posBearing={trackingPosition?.bearing}
          tracking={isTracking}
          onStopTracking={onStopTracking}
          onMapMoved={handleMapMoved}
        />
        <CustomZoomControl map={mapInstance} />
        <MapLoadingOverlay isLoading={isLoading} />

        {/* <button
        className="map-control-btn-bbox"
        onClick={handleFitBounds}
        title="Center Fleet View"
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
        >
          <path d="M15 3h6v6M9 21H3v-6M21 15v6h-6M3 9V3h6" />
          <path d="M21 3l-7 7M3 21l7-7M3 3l7 7M21 21l-7-7" />
        </svg>
      </button> */}

        <button
          className={`map-control-btn-locate ${showRecenter && hasValidFleetLocation ? "visible" : "hidden"}`}
          onClick={handleRecenterVehicle}
          title="Go to selected vehicle"
          disabled={!selectedFleet}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M12 2v3m0 14v3m10-10h-3M5 12H2" />
          </svg>
        </button>
      </div>
    );
  },
);

export default MapContainer;
